import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../catalyst/description-list'
import { Description, Field, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading, Subheading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { api } from '../lib/api'
import type { AdminPesticide, AdminPesticideLabel, AdminUsePattern } from '../lib/types'
import { HistoryList, ReviewBadge, ReviewPanel } from './review'
import { Shell } from './shell'

type LabelDraft = {
  signalWord: string
  reiHours: string
  reiText: string
  resistanceManagementGroup: string
  ppeMixingLoading: string
  ppeApplication: string
  environmentalHazardsText: string
}

const toLabelDraft = (l: AdminPesticideLabel): LabelDraft => ({
  signalWord: l.signalWord ?? '',
  reiHours: l.reiHours === null ? '' : String(l.reiHours),
  reiText: l.reiText ?? '',
  resistanceManagementGroup: l.resistanceManagementGroup ?? '',
  ppeMixingLoading: l.ppeMixingLoading ?? '',
  ppeApplication: l.ppeApplication ?? '',
  environmentalHazardsText: l.environmentalHazardsText ?? '',
})

const orNull = (v: string) => (v.trim() === '' ? null : v.trim())
const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v))

type PatternDraft = Record<keyof Omit<AdminUsePattern, 'id'>, string>

const EMPTY_PATTERN: PatternDraft = {
  cropRaw: '', cropGroup: '', pestRaw: '', applicationRateText: '', applicationRateMin: '', applicationRateMax: '', applicationRateUnit: '',
  applicationMethod: '', timingText: '', phiDays: '', minRetreatmentIntervalDays: '', maxApplicationsPerSeason: '', restrictionsText: '', sourceExcerpt: '',
}

function toPatternDraft(p: AdminUsePattern): PatternDraft {
  return Object.fromEntries(Object.keys(EMPTY_PATTERN).map((k) => [k, p[k as keyof AdminUsePattern] === null ? '' : String(p[k as keyof AdminUsePattern])])) as PatternDraft
}

function patternBody(d: PatternDraft) {
  return {
    cropRaw: d.cropRaw.trim(),
    cropGroup: orNull(d.cropGroup),
    pestRaw: orNull(d.pestRaw),
    applicationRateText: d.applicationRateText.trim(),
    applicationRateMin: numOrNull(d.applicationRateMin),
    applicationRateMax: numOrNull(d.applicationRateMax),
    applicationRateUnit: orNull(d.applicationRateUnit),
    applicationMethod: orNull(d.applicationMethod),
    timingText: orNull(d.timingText),
    phiDays: numOrNull(d.phiDays),
    minRetreatmentIntervalDays: numOrNull(d.minRetreatmentIntervalDays),
    maxApplicationsPerSeason: numOrNull(d.maxApplicationsPerSeason),
    restrictionsText: orNull(d.restrictionsText),
    sourceExcerpt: d.sourceExcerpt.trim(),
  }
}

function PatternDialog({ initial, onClose, onSave }: { initial: PatternDraft | null; onClose: () => void; onSave: (body: ReturnType<typeof patternBody>) => Promise<void> }) {
  const [d, setD] = useState<PatternDraft>(initial ?? EMPTY_PATTERN)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const f = (key: keyof PatternDraft, label: string, opts: { type?: string; textarea?: boolean; required?: boolean; hint?: string } = {}) => (
    <Field>
      <Label>{label}</Label>
      {opts.textarea ? (
        <Textarea rows={3} value={d[key]} required={opts.required} onChange={(e) => setD({ ...d, [key]: e.target.value })} />
      ) : (
        <Input type={opts.type ?? 'text'} step="any" value={d[key]} required={opts.required} onChange={(e) => setD({ ...d, [key]: e.target.value })} />
      )}
      {opts.hint && <Description>{opts.hint}</Description>}
    </Field>
  )

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onSave(patternBody(d))
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} size="3xl">
      <form onSubmit={submit}>
        <DialogTitle>{initial ? 'Edit use pattern' : 'Add use pattern'}</DialogTitle>
        <DialogBody>
          <FieldGroup>
            <div className="grid gap-4 sm:grid-cols-2">
              {f('cropRaw', 'Crop (as the label words it)', { required: true })}
              {f('cropGroup', 'Crop group', { hint: 'e.g. BLUEBERRY. Empty if none.' })}
              {f('pestRaw', 'Pest')}
              {f('applicationMethod', 'Method')}
            </div>
            {f('applicationRateText', 'Rate, as labelled', { required: true })}
            <div className="grid gap-4 sm:grid-cols-3">
              {f('applicationRateMin', 'Rate min', { type: 'number' })}
              {f('applicationRateMax', 'Rate max', { type: 'number' })}
              {f('applicationRateUnit', 'Rate unit')}
              {f('phiDays', 'PHI (days)', { type: 'number' })}
              {f('minRetreatmentIntervalDays', 'Re-treatment interval (days)', { type: 'number' })}
              {f('maxApplicationsPerSeason', 'Max applications / season', { type: 'number' })}
            </div>
            {f('timingText', 'Timing', { textarea: true })}
            {f('restrictionsText', 'Restrictions', { textarea: true })}
            {f('sourceExcerpt', 'Label text this comes from', { textarea: true, required: true, hint: 'Quote the label. Every value must be backed by it; nothing is invented.' })}
          </FieldGroup>
          {error && <Text className="mt-4 text-red-600 dark:text-red-400">{error}</Text>}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function PesticidePage() {
  const { pesticideId } = useParams()
  const [product, setProduct] = useState<AdminPesticide | null>(null)
  const [draft, setDraft] = useState<LabelDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [dialog, setDialog] = useState<{ pattern: AdminUsePattern | null } | null>(null)

  function adopt(next: AdminPesticide) {
    setProduct(next)
    setDraft(next.label ? toLabelDraft(next.label) : null)
  }

  useEffect(() => {
    setProduct(null)
    api
      .get<AdminPesticide>(`/admin/pesticides/${pesticideId}`)
      .then(adopt)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load this product.'))
  }, [pesticideId])

  async function run(fn: () => Promise<AdminPesticide>) {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      adopt(await fn())
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }
  const patchLabel = (body: Record<string, unknown>) => run(() => api.patch<AdminPesticide>(`/admin/pesticides/${pesticideId}/label`, body))

  const back = (
    <Link href="/pesticides" className="text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
      All pesticides
    </Link>
  )
  if (!product) {
    return (
      <Shell>
        {back}
        <Text className="mt-6">{error ?? 'Loading…'}</Text>
      </Shell>
    )
  }
  const label = product.label

  return (
    <Shell>
      {back}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Heading>{product.productNameEn}</Heading>
        {label && <ReviewBadge status={label.reviewStatus} />}
        {label?.curatedAt && <Badge color="blue">Edited by staff</Badge>}
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-10">
          <section>
            <Subheading>Registry (Health Canada, read-only)</Subheading>
            <DescriptionList className="mt-3">
              <DescriptionTerm>Registration #</DescriptionTerm>
              <DescriptionDetails>{product.registrationNumber}</DescriptionDetails>
              <DescriptionTerm>Status</DescriptionTerm>
              <DescriptionDetails>{product.registrationStatus ?? '—'} · {product.currentOrHistorical}</DescriptionDetails>
              <DescriptionTerm>Active ingredients</DescriptionTerm>
              <DescriptionDetails>{product.activeIngredientsRawEn ?? '—'}</DescriptionDetails>
              <DescriptionTerm>Registrant</DescriptionTerm>
              <DescriptionDetails>{product.registrantName ?? '—'}</DescriptionDetails>
              <DescriptionTerm>Type</DescriptionTerm>
              <DescriptionDetails>{product.productType ?? '—'}</DescriptionDetails>
              <DescriptionTerm>Sites of use</DescriptionTerm>
              <DescriptionDetails>{product.sitesOfUse ?? '—'}</DescriptionDetails>
            </DescriptionList>
          </section>

          {!label || !draft ? (
            <Text>There is no label to review yet: it is fetched and read the first time a grower opens this product.</Text>
          ) : (
            <>
              <form
                className="space-y-6"
                onSubmit={(event) => {
                  event.preventDefault()
                  void patchLabel({
                    signalWord: draft.signalWord || null,
                    reiHours: numOrNull(draft.reiHours),
                    reiText: orNull(draft.reiText),
                    resistanceManagementGroup: orNull(draft.resistanceManagementGroup),
                    ppeMixingLoading: orNull(draft.ppeMixingLoading),
                    ppeApplication: orNull(draft.ppeApplication),
                    environmentalHazardsText: orNull(draft.environmentalHazardsText),
                  })
                }}
              >
                <Subheading>Label facts</Subheading>
                <Text>
                  Read from the label by a model. Correct anything that does not match the label. Once you save a change, the label is yours: it will not be re-read automatically.
                </Text>
                <div className="grid gap-6 sm:grid-cols-3">
                  <Field>
                    <Label>Signal word</Label>
                    <Select value={draft.signalWord} onChange={(e) => setDraft({ ...draft, signalWord: e.target.value })}>
                      <option value="">None stated</option>
                      {['CAUTION', 'WARNING', 'DANGER', 'POISON'].map((w) => (
                        <option key={w}>{w}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    <Label>REI (hours)</Label>
                    <Input type="number" min={0} value={draft.reiHours} onChange={(e) => setDraft({ ...draft, reiHours: e.target.value })} />
                  </Field>
                  <Field>
                    <Label>REI, if not a number</Label>
                    <Input value={draft.reiText} onChange={(e) => setDraft({ ...draft, reiText: e.target.value })} />
                  </Field>
                </div>
                {([
                  ['resistanceManagementGroup', 'Resistance management group', false],
                  ['ppeMixingLoading', 'PPE: mixing and loading', true],
                  ['ppeApplication', 'PPE: application', true],
                  ['environmentalHazardsText', 'Environmental hazards', true],
                ] as const).map(([key, text, area]) => (
                  <Field key={key}>
                    <Label>{text}</Label>
                    {area ? (
                      <Textarea rows={3} value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
                    ) : (
                      <Input value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
                    )}
                  </Field>
                ))}
                {error && <Text className="text-red-600 dark:text-red-400">{error}</Text>}
                <div className="flex items-center gap-3">
                  <Button type="submit" disabled={busy}>
                    {busy ? 'Saving…' : 'Save label facts'}
                  </Button>
                  {saved && <Text className="!mt-0">Saved.{label.reviewStatus === 'unreviewed' ? ' It needs review again.' : ''}</Text>}
                </div>
              </form>

              <section>
                <div className="flex items-center justify-between">
                  <Subheading>Use patterns ({product.usePatterns.length})</Subheading>
                  <Button outline onClick={() => setDialog({ pattern: null })}>
                    Add
                  </Button>
                </div>
                {product.usePatterns.length === 0 ? (
                  <Text className="mt-2">No crop entries were read from this label.</Text>
                ) : (
                  <Table className="mt-3" striped>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Crop</TableHeader>
                        <TableHeader>Rate</TableHeader>
                        <TableHeader>PHI</TableHeader>
                        <TableHeader>Label text</TableHeader>
                        <TableHeader className="w-0" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {product.usePatterns.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium text-zinc-950 dark:text-white">
                            {p.cropRaw}
                            {p.pestRaw && <span className="block text-zinc-500 dark:text-zinc-400">{p.pestRaw}</span>}
                          </TableCell>
                          <TableCell>{p.applicationRateText}</TableCell>
                          <TableCell>{p.phiDays === null ? '—' : `${p.phiDays} d`}</TableCell>
                          <TableCell className="max-w-xs whitespace-normal text-xs text-zinc-500 dark:text-zinc-400">{p.sourceExcerpt}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button plain onClick={() => setDialog({ pattern: p })}>
                                Edit
                              </Button>
                              <Button
                                plain
                                disabled={busy}
                                onClick={() => {
                                  if (confirm(`Remove the ${p.cropRaw} entry?`)) void run(() => api.delete<AdminPesticide>(`/admin/pesticides/${pesticideId}/use-patterns/${p.id}`))
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </section>
            </>
          )}
        </div>

        {label && (
          <div className="space-y-8">
            <ReviewPanel
              key={label.reviewStatus + label.reviewedAt}
              status={label.reviewStatus}
              notes={label.reviewNotes}
              reviewedAt={label.reviewedAt}
              busy={busy}
              onReview={(review) => void patchLabel({ review })}
            />
            <HistoryList history={product.history} />
          </div>
        )}
      </div>

      {dialog && (
        <PatternDialog
          key={dialog.pattern?.id ?? 'new'}
          initial={dialog.pattern ? toPatternDraft(dialog.pattern) : null}
          onClose={() => setDialog(null)}
          onSave={async (body) => {
            const next = dialog.pattern
              ? await api.patch<AdminPesticide>(`/admin/pesticides/${pesticideId}/use-patterns/${dialog.pattern.id}`, body)
              : await api.post<AdminPesticide>(`/admin/pesticides/${pesticideId}/use-patterns`, body)
            adopt(next)
          }}
        />
      )}
    </Shell>
  )
}
